// Meta Pixel integration helpers.
// Base pixel + initial PageView are loaded in public/index.html.
// This module adds:
//   1) SPA PageView tracking on route changes (no duplicate init).
//   2) Global document-level click delegation that fires a custom
//      "TelegramClick" event for ANY link pointing to Telegram.

const TELEGRAM_HOST_PATTERNS = [
  /(^|\.)t\.me$/i,
  /(^|\.)telegram\.me$/i,
  /(^|\.)telegram\.dog$/i,
];
const TELEGRAM_PROTOCOLS = ["tg:", "telegram:"];

function isTelegramHref(href) {
  if (!href || typeof href !== "string") return false;
  const trimmed = href.trim();
  // Direct deep links (tg://, telegram://)
  for (const p of TELEGRAM_PROTOCOLS) {
    if (trimmed.toLowerCase().startsWith(p)) return true;
  }
  // Bare host (t.me/... without protocol) — be lenient
  if (/^(https?:\/\/)?(www\.)?(t\.me|telegram\.me|telegram\.dog)\b/i.test(trimmed)) {
    return true;
  }
  // Full URL — parse host
  try {
    const u = new URL(trimmed, window.location.href);
    return TELEGRAM_HOST_PATTERNS.some((re) => re.test(u.hostname));
  } catch {
    return false;
  }
}

function callFbq(...args) {
  if (typeof window === "undefined") return;
  const fbq = window.fbq;
  if (typeof fbq === "function") {
    try {
      fbq(...args);
      // Temporary diagnostic log (safe — only event name, no PII)
      // eslint-disable-next-line no-console
      console.info("[Pixel] fbq called:", args.join(" "));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[Pixel] fbq threw:", err);
    }
  } else {
    // eslint-disable-next-line no-console
    console.warn("[Pixel] fbq is not available — call dropped:", args.join(" "));
  }
}

export function trackPageView() {
  callFbq("track", "PageView");
}

export function trackTelegramClick() {
  callFbq("trackCustom", "TelegramClick");
}

// Install a single capture-phase delegated listener on document.
// Capture phase guarantees we fire once per real click, before the
// browser follows the link. The pixel request is fire-and-forget
// (image beacon), so we do NOT need to delay navigation.
let installed = false;
export function installTelegramClickTracker() {
  if (installed || typeof document === "undefined") return;
  installed = true;

  const handler = (event) => {
    // Ignore if the event was already handled (defensive — single listener,
    // but React synthetic events could bubble separately).
    if (event.__telegramTracked) return;

    // Find nearest <a> ancestor
    const anchor = event.target && event.target.closest
      ? event.target.closest("a[href]")
      : null;
    if (!anchor) return;

    const href = anchor.getAttribute("href");
    if (!isTelegramHref(href)) return;

    event.__telegramTracked = true;
    trackTelegramClick();
  };

  // Use capture phase so we fire before any stopPropagation downstream.
  document.addEventListener("click", handler, true);
  // Also catch keyboard activation (Enter) and auxclick (middle click) on links.
  document.addEventListener("auxclick", handler, true);
}
